import { Intent } from '@blueprintjs/core';
import {
  db as stardogDb,
  query as stardogQuery,
  virtualGraphs as stardogVirtualGraphs,
} from 'stardog';
import { MimeType } from 'vet-bones/constants';

import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { request } from 'src/actions/request/request';
import { ActionCreatorAction, createTypedAction } from 'src/actions/types';
import { StoredQuery } from 'src/constants/storedQuery';
import { getNamedGraphsQuery } from 'src/templates/queries/namedGraphs';
import { serializeStoredQuery } from 'src/utils/jsonld/serializeStoredQuery';

export const databasesActionCreators = {
  refreshDatabasesStarted: () =>
    createTypedAction(DatabasesActionType.REFRESH_DATABASES_STARTED),
  refreshDatabasesFinished: () =>
    createTypedAction(DatabasesActionType.REFRESH_DATABASES_FINISHED),

  refreshDatabaseStarted: (databaseId: string) =>
    createTypedAction(DatabasesActionType.REFRESH_DATABASE_STARTED, {
      databaseId,
    }),
  refreshDatabaseFinished: (databaseId: string) =>
    createTypedAction(DatabasesActionType.REFRESH_DATABASE_FINISHED, {
      databaseId,
    }),
  addModelFileToDatabaseStarted: (databaseId: string) =>
    createTypedAction(DatabasesActionType.ADD_MODEL_FILE_TO_DATABASE_STARTED, {
      databaseId,
    }),
  addModelFileToDatabaseFinished: ({
    databaseId,
    intent,
  }: {
    databaseId: string;
    intent: Intent;
  }) =>
    createTypedAction(DatabasesActionType.ADD_MODEL_FILE_TO_DATABASE_FINISHED, {
      databaseId,
      intent,
    }),
};

export const databasesStardogRequestDispatchers = {
  createDatabase: async (
    databaseId: string,
    databaseOptions?: Record<string, unknown>
  ) =>
    request({
      api: stardogDb.create,
      args: [databaseId, databaseOptions],
      attempt: DatabasesActionType.CREATE_DATABASE_ATTEMPT,
      failure: DatabasesActionType.CREATE_DATABASE_FAILURE,
      success: DatabasesActionType.CREATE_DATABASE_SUCCESS,
      action: { databaseId, databaseOptions },
    }),
  requestDatabases: async () =>
    request({
      api: stardogDb.list,
      args: [],
      attempt: DatabasesActionType.REQUEST_DATABASES_ATTEMPT,
      failure: DatabasesActionType.REQUEST_DATABASES_FAILURE,
      success: DatabasesActionType.REQUEST_DATABASES_SUCCESS,
      action: {},
    }),
  requestDatabasesInfo: async () =>
    request({
      api: stardogDb.listInfo,
      args: [],
      attempt: DatabasesActionType.REQUEST_DATABASES_INFO_ATTEMPT,
      failure: DatabasesActionType.REQUEST_DATABASES_INFO_FAILURE,
      success: DatabasesActionType.REQUEST_DATABASES_INFO_SUCCESS,
      action: {},
    }),
  requestDatabaseDetails: async (
    databaseId: string,
    shouldNotDispatch?: boolean
  ) =>
    request({
      api: stardogDb.options.getAll,
      args: [databaseId],
      attempt: DatabasesActionType.REQUEST_DATABASE_DETAILS_ATTEMPT,
      failure: DatabasesActionType.REQUEST_DATABASE_DETAILS_FAILURE,
      success: DatabasesActionType.REQUEST_DATABASE_DETAILS_SUCCESS,
      action: { databaseId },
      shouldNotDispatch,
    }),
  updateDatabaseDetails: async (
    databaseId: string,
    details: Record<string, string | number | boolean | string[]>
  ) =>
    request({
      api: stardogDb.options.set,
      args: [databaseId, details],
      attempt: DatabasesActionType.UPDATE_DATABASE_DETAILS_ATTEMPT,
      failure: DatabasesActionType.UPDATE_DATABASE_DETAILS_FAILURE,
      success: DatabasesActionType.UPDATE_DATABASE_DETAILS_SUCCESS,
      action: { databaseId, details },
    }),
  requestDatabaseNamespaces: async (databaseId: string) =>
    request({
      api: stardogDb.namespaces.get,
      args: [databaseId],
      attempt: DatabasesActionType.REQUEST_DATABASE_NAMESPACES_ATTEMPT,
      failure: DatabasesActionType.REQUEST_DATABASE_NAMESPACES_FAILURE,
      success: DatabasesActionType.REQUEST_DATABASE_NAMESPACES_SUCCESS,
      action: { databaseId },
    }),
  beginDatabaseTransaction: async (databaseId: string) =>
    request({
      api: stardogDb.transaction.begin,
      args: [databaseId],
      attempt: DatabasesActionType.BEGIN_DATABASE_TRANSACTION_ATTEMPT,
      failure: DatabasesActionType.BEGIN_DATABASE_TRANSACTION_FAILURE,
      success: DatabasesActionType.BEGIN_DATABASE_TRANSACTION_SUCCESS,
      action: { databaseId },
    }),
  commitDatabaseTransaction: async (
    databaseId: string,
    transactionId: string
  ) =>
    request({
      api: stardogDb.transaction.commit,
      args: [databaseId, transactionId],
      attempt: DatabasesActionType.COMMIT_DATABASE_TRANSACTION_ATTEMPT,
      failure: DatabasesActionType.COMMIT_DATABASE_TRANSACTION_FAILURE,
      success: DatabasesActionType.COMMIT_DATABASE_TRANSACTION_SUCCESS,
      action: { databaseId, transactionId },
    }),
  rollbackDatabaseTransaction: async (
    databaseId: string,
    transactionId: string
  ) =>
    request({
      api: stardogDb.transaction.rollback,
      args: [databaseId, transactionId],
      attempt: DatabasesActionType.ROLLBACK_DATABASE_TRANSACTION_ATTEMPT,
      failure: DatabasesActionType.ROLLBACK_DATABASE_TRANSACTION_FAILURE,
      success: DatabasesActionType.ROLLBACK_DATABASE_TRANSACTION_SUCCESS,
      action: { databaseId, transactionId },
    }),
  addDataToDatabase: async ({
    databaseId,
    transactionId,
    contents,
    mimeType,
    namedGraph,
  }: {
    databaseId: string;
    transactionId: string;
    contents: string;
    mimeType: string;
    namedGraph?: string;
  }) =>
    request({
      api: stardogDb.add,
      args: [
        databaseId,
        transactionId,
        contents,
        { contentType: mimeType },
        namedGraph ? { graphUri: namedGraph } : undefined,
      ],
      attempt: DatabasesActionType.ADD_DATA_TO_DATABASE_ATTEMPT,
      failure: DatabasesActionType.ADD_DATA_TO_DATABASE_FAILURE,
      success: DatabasesActionType.ADD_DATA_TO_DATABASE_SUCCESS,
      action: { databaseId, transactionId },
    }),
  clearDatabaseNamedGraph: async ({
    databaseId,
    transactionId,
    namedGraph,
  }: {
    databaseId: string;
    transactionId?: string;
    namedGraph: string;
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        `CLEAR GRAPH <${namedGraph}>`,
        'text/boolean',
        transactionId ? { txid: transactionId } : undefined,
      ],
      attempt: DatabasesActionType.CLEAR_DATABASE_NAMED_GRAPH_ATTEMPT,
      failure: DatabasesActionType.CLEAR_DATABASE_NAMED_GRAPH_FAILURE,
      success: DatabasesActionType.CLEAR_DATABASE_NAMED_GRAPH_SUCCESS,
      action: { databaseId, namedGraph, transactionId },
    }),
  createVirtualGraph: async ({
    databaseId,
    dataSource,
    mappings,
    properties,
    vgId,
  }: {
    databaseId: string;
    dataSource: string;
    mappings: string;
    properties: Record<string, any>;
    vgId: string;
  }) =>
    request({
      api: stardogVirtualGraphs.add,
      args: [
        vgId,
        mappings,
        {
          ...properties,
          'mappings.syntax': 'sms2',
        },
        { db: databaseId, dataSource },
      ],
      attempt: DatabasesActionType.CREATE_VIRTUAL_GRAPH_ATTEMPT,
      failure: DatabasesActionType.CREATE_VIRTUAL_GRAPH_FAILURE,
      success: DatabasesActionType.CREATE_VIRTUAL_GRAPH_SUCCESS,
      action: { databaseId, dataSource, vgId },
    }),
  updateVirtualGraph: async ({
    databaseId,
    dataSource,
    mappings,
    properties,
    vgId,
  }: {
    databaseId: string;
    dataSource: string;
    mappings: string;
    properties: Record<string, any>;
    vgId: string;
  }) =>
    request({
      api: stardogVirtualGraphs.update,
      args: [
        vgId,
        mappings,
        {
          ...properties,
          'mappings.syntax': 'sms2',
        },
        { db: databaseId, dataSource },
      ],
      attempt: DatabasesActionType.UPDATE_VIRTUAL_GRAPH_ATTEMPT,
      failure: DatabasesActionType.UPDATE_VIRTUAL_GRAPH_FAILURE,
      success: DatabasesActionType.UPDATE_VIRTUAL_GRAPH_SUCCESS,
      action: { dataSource, vgId },
    }),
  requestNamedGraphs: async (databaseId: string) => {
    return request({
      api: stardogQuery.execute,
      args: [databaseId, getNamedGraphsQuery],
      attempt: DatabasesActionType.REQUEST_NAMED_GRAPHS_ATTEMPT,
      success: DatabasesActionType.REQUEST_NAMED_GRAPHS_SUCCESS,
      failure: DatabasesActionType.REQUEST_NAMED_GRAPHS_FAILURE,
      action: { databaseId },
    });
  },
  importCsv: async ({
    file,
    fileIri,
    databaseId,
    mappings,
    namedGraph,
    properties,
  }: {
    file: File;
    fileIri: string;
    databaseId: string;
    mappings: string;
    namedGraph: string;
    properties: string;
  }) =>
    request({
      api: stardogVirtualGraphs.importFile,
      args: [
        file,
        fileIri,
        'DELIMITED',
        databaseId,
        {
          mappings,
          namedGraph,
          properties,
        },
      ],
      attempt: DatabasesActionType.IMPORT_CSV_ATTEMPT,
      failure: DatabasesActionType.IMPORT_CSV_FAILURE,
      success: DatabasesActionType.IMPORT_CSV_SUCCESS,
      action: { databaseId, file, namedGraph },
    }),

  listStoredQueries: async () =>
    request({
      api: stardogQuery.stored.list,
      args: [{ accept: MimeType.JSON_LD }],
      attempt: DatabasesActionType.LIST_STORED_QUERIES_ATTEMPT,
      failure: DatabasesActionType.LIST_STORED_QUERIES_FAILURE,
      success: DatabasesActionType.LIST_STORED_QUERIES_SUCCESS,
      action: {},
    }),
  createStoredQuery: async (storedQuery: StoredQuery) => {
    const serializedQuery = serializeStoredQuery(storedQuery);
    return request({
      api: stardogQuery.stored.create,
      args: [
        serializedQuery,
        { accept: MimeType.JSON_LD, contentType: MimeType.JSON_LD },
      ],
      attempt: DatabasesActionType.CREATE_STORED_QUERY_ATTEMPT,
      failure: DatabasesActionType.CREATE_STORED_QUERY_FAILURE,
      success: DatabasesActionType.CREATE_STORED_QUERY_SUCCESS,
      action: { storedQuery },
    });
  },
  updateStoredQuery: async (storedQuery: StoredQuery) => {
    const serializedQuery = serializeStoredQuery(storedQuery);
    return request({
      api: stardogQuery.stored.update,
      args: [
        serializedQuery,
        { accept: MimeType.JSON_LD, contentType: MimeType.JSON_LD },
      ],
      attempt: DatabasesActionType.UPDATE_STORED_QUERY_ATTEMPT,
      failure: DatabasesActionType.UPDATE_STORED_QUERY_FAILURE,
      success: DatabasesActionType.UPDATE_STORED_QUERY_SUCCESS,
      action: { storedQuery },
    });
  },
};

export type DatabasesAction = ActionCreatorAction<
  typeof databasesActionCreators & typeof databasesStardogRequestDispatchers
>;
